import React, { useEffect } from 'react';
import { groupBy } from 'lodash';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { getResources, useRedirect, useDataProvider } from 'react-admin';
import { NavLink, matchPath } from 'react-router-dom';

import Box from '@material-ui/core/Box';
import MuiAppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Toolbar from '@material-ui/core/Toolbar';
import makeStyles from '@material-ui/core/styles/makeStyles';

import UserMenu from './UserMenu';
import MobileMenu from './MobileMenu';
import MarketRefreshButton from './MarketRefreshButton';
import AppBarTab from './AppBarTab';
import logo from 'rollo.png';
import { openVerification } from 'store/actions/verificationActions';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
  },

  toolbar: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    boxShadow: theme.shadows[1],
  },

  logo: {
    width: theme.spacing(9),
    marginBottom: 8,
    [theme.breakpoints.down('xl')]: {
      marginRight: theme.spacing(8),
    },
    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(4),
    },
  },

  tabs: {
    width: '100%',
  },

  tabsContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '3px',
  },

  tab: {
    '&.MuiTab-root': {
      [theme.breakpoints.down('xl')]: {
        marginRight: theme.spacing(5),
        display: 'flex',
      },
      [theme.breakpoints.down('md')]: {
        marginRight: theme.spacing(2.5),
        display: 'none',
      },
    },
  },

  tabContainer: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },

  buttons: {
    display: 'flex',
    alignItems: 'center',

    '& > *': {
      [theme.breakpoints.down('xl')]: {
        marginRight: theme.spacing(4),
      },
      [theme.breakpoints.down('md')]: {
        marginRight: theme.spacing(1),
      },
    },
  },

  actionTab: {
    '&:not(:last-child)': {
      [theme.breakpoints.down('xl')]: {
        marginRight: theme.spacing(4),
      },
      [theme.breakpoints.down('md')]: {
        marginRight: theme.spacing(1),
      },
    },
  },

  activeTab: {
    color: theme.palette.primary.main,
  },
}));

const NonTab = ({ className, children }: { className?: string; children?: React.ReactNode }) => (
  <div className={className}>{children}</div>
);

const complexMenus = [
  { label: 'Connections', value: 'connections' },
  // {label: 'Settings', value: 'settings'},
];

const settingTabs = ['shippingPresets', 'warehousePresets'];

const AppBarTabs = ({ items, activeIndex }: { items: any; activeIndex: string }) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const redirect = useRedirect();

  const handleCalculatorClick = (item) => {
    const phoneVerified = JSON.parse(sessionStorage.getItem('phoneVerified') as string);
    if (!phoneVerified && typeof phoneVerified === 'boolean') {
      dispatch(openVerification());
      return;
    }
    redirect(item.path);
  };

  return (
    <Box className={classes.tabsContainer}>
      <Box className={classes.tabContainer}>
        {items.primary &&
          items.primary.map((item) => (
            <AppBarTab
              component={NavLink}
              key={item.path}
              value={item.index}
              to={item.path}
              label={item.label}
              className={classes.tab}
              selected={activeIndex === item.index}
            />
          ))}
        {complexMenus.map((item) => (
          <AppBarTab
            className={classes.tab}
            key={item.value}
            label={item.label}
            value={item.value}
            submenu={items[item.value]}
            selected={activeIndex === item.value}
          />
        ))}
      </Box>
      <Box className={classes.tabContainer}>
        {items.actions &&
          items.actions.map((item) => (
            <AppBarTab
              className={classes.actionTab}
              key={item.path}
              value={item.index}
              to={item.path}
              label={item.label}
              onClick={() => handleCalculatorClick(item)}
              selected={activeIndex === item.index}
            />
          ))}
        <NonTab className={classes.buttons}>
          <MarketRefreshButton />
        </NonTab>
        <UserMenu />
        <MobileMenu items={items} activeIndex={activeIndex} />
        {items.help &&
          items.help.map((item) => (
            <AppBarTab
              className={classes.actionTab}
              component={NavLink}
              key={item.path}
              value={item.index}
              to={item.path}
              label={item.label}
            />
          ))}
      </Box>
    </Box>
  );
};

const AppBar = () => {
  const classes = useStyles();
  const dataProvider = useDataProvider();

  // fetch domestic countries on app bar mount
  useEffect(() => {
    dataProvider.getList('domesticCountries');
  }, [dataProvider]);

  const getIndex = (items, menus) => {
    const found = items.find((item) => matchPath(window.location.pathname, item));
    return found ? found.index : false;
  };

  const allItems = [...useSelector((state) => getResources(state), shallowEqual)]
    .filter((resource) => resource.options.menu)
    .map((resource) => {
      const { label, menu, order = 100 } = resource.options;
      return {
        index: complexMenus.find((item) => item.value === menu) ? menu : resource.name,
        path: `/${resource.name}`,
        label,
        menu,
        order,
      };
    })
    .sort((a, b) => a.order - b.order);

  const menus = groupBy(allItems, 'menu');

  let menuIndex = getIndex(allItems, menus);
  if (settingTabs.includes(menuIndex)) menuIndex = false;

  return (
    <MuiAppBar
      className={classes.root}
      color="secondary"
      position="static"
      elevation={0}
      data-test="top-menu">
      <Toolbar className={classes.toolbar}>
        <img className={classes.logo} src={logo} alt="Rollo Logo" />
        <Tabs
          className={classes.tabs}
          value={menuIndex}
          indicatorColor="primary"
          textColor="primary">
          <AppBarTabs items={menus} activeIndex={menuIndex} />
        </Tabs>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar;
